import { useEffect, useId, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { Question } from '../../../config/types';
import Details from '../Details';
import ErrorMessage from '../ErrorMessage';
import GradeInputColumn from './GradeInputColumn';
import { dateIsEmpty } from './utils';

interface GradeInputProps {
  name: string;
  defaultValue?: string; // An ISO date string with only the yyyy-mm-dd
  disabled?: boolean;
  dayLabel?: string;
  monthLabel?: string;
  yearLabel?: string;
  anchorId?: string;
  onChange?(value: string): void;
}

export function GradeInputWrapperColumn({
  defaultValue,
  disabled = false,
  anchorId = useId(),
  onChange,
}: GradeInputProps) {
  const t1Id = useMemo(() => `${anchorId}`, [anchorId]);
  const t2Id = useMemo(() => `${anchorId}2`, [anchorId]);
  const t3Id = useMemo(() => `${anchorId}3`, [anchorId]);
  const t4Id = useMemo(() => `${anchorId}4`, [anchorId]);

  const values = useMemo<string[]>(() => {
    const initValue = defaultValue ?? '--';
    return initValue.split('-');
  }, [defaultValue]);
  const [t1Value, setT1Value] = useState(values[0]);
  const [t2Value, setT2Value] = useState(values[1]);
  const [t3Value, setT3Value] = useState(values[2]);
  const [t4Value, setT4Value] = useState(values[3]);

  const triggerChange = (newValue: string) => {
    if (onChange) {
      if (dateIsEmpty(newValue)) {
        onChange('');
      } else {
        onChange(newValue);
      }
    }
  };

  const handleDayChange = (day: string) => {
    setT1Value(day);
    triggerChange(`${day}-${t2Value}-${t3Value}-${t4Value}`);
  };

  const handleMonthChange = (month: string) => {
    setT2Value(month);
    triggerChange(`${t1Value}-${month}-${t3Value}-${t4Value}`);
  };

  const handleYearChange = (year: string) => {
    setT3Value(year);
    triggerChange(`${t1Value}-${t2Value}-${year}-${t4Value}`);
  };
  const handleG7Change = (value: string) => {
    setT4Value(value);
    triggerChange(`${t1Value}-${t2Value}-${t3Value}-${value}`);
  };

  return (
    <div>
      <table className="govuk-table">
        <thead className="govuk-table__head">
          <tr className="govuk-table__row">
            <th scope="col" className="govuk-table__header">
              Grade
            </th>
            <th scope="col" className="govuk-table__header">
              Position adversised
            </th>
            <th scope="col" className="govuk-table__header">
              Total applications
            </th>
          </tr>
        </thead>
        <tbody className="govuk-table__body">
          <tr className="govuk-table__row">
            <th scope="row" className="govuk-table__header">
              SCS 2
            </th>
            <td className="govuk-table__cell">
              <GradeInputColumn
                testId={'scs2-pa'}
                disabled={disabled}
                anchorId={t3Id}
                onChange={handleDayChange}
                value={t1Value}
              />
            </td>
            <td className="govuk-table__cell">
              <GradeInputColumn
                testId={'scs2-ta'}
                disabled={disabled}
                anchorId={t2Id}
                onChange={handleMonthChange}
                value={t2Value}
              />
            </td>
          </tr>
          <tr className="govuk-table__row">
            <th scope="row" className="govuk-table__header">
              SCS 1
            </th>
            <td className="govuk-table__cell">
              <GradeInputColumn
                testId={'scs1-pa'}
                disabled={disabled}
                anchorId={t1Id}
                onChange={handleYearChange}
                value={t3Value}
              />
            </td>
            <td className="govuk-table__cell">
              <GradeInputColumn
                testId={'scs1-ta'}
                disabled={disabled}
                anchorId={t4Id}
                onChange={handleG7Change}
                value={t4Value}
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

interface GradeInputFieldProps {
  question: Question;
  disabled?: boolean;
  anchorId: string;
  detailsHeading?: string;
  detailsContent?: string;
}

export function GradeInputFieldCoulmnwise({
  question,
  disabled,
  anchorId,
  detailsHeading,
  detailsContent,
}: GradeInputFieldProps) {
  const { register, getFieldState, getValues, setValue } = useFormContext();
  const { error } = getFieldState(question.name);

  useEffect(() => {
    // If the value is currently not defined in the form, initialise it
    if (getValues(question.name) === undefined) {
      setValue(question.name, '');
    }
  }, [getValues, question.name, setValue]);

  const { onChange, name } = register(question.name);

  const handleChange = (dateChange: string) => {
    onChange({ target: { name, value: dateChange } });
  };

  return (
    <>
      {error && <ErrorMessage error={error} />}
      {detailsHeading && <Details heading={detailsHeading} content={detailsContent} />}

      <div className="govuk-date-input">
        <GradeInputWrapperColumn
          name={question.name}
          disabled={disabled}
          anchorId={anchorId}
          onChange={handleChange}
          defaultValue={getValues(question.name)}
        />
      </div>
    </>
  );
}
